import React, { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { negate } from 'lodash/fp'
import {
	CopyIcon,
	EditIcon,
	EllipsisVerticalIcon,
	PlusIcon,
	Trash2Icon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useOptimizationIsReadyForChange } from '@/entities/optimization'
import { useDeleteOptimizationGroupMutation } from '@/entities/optimization-group'
import { isGlobalPricingCampaign } from '@/entities/pricing-campaign'
import { useAccess } from '@/entities/session'
import { useCreateSettingsTemplateMutation } from '@/entities/settings-template'

import { client } from '@/shared/api/client'
import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { LinkButton } from '@/shared/components/ui/link-button'
import { SettingsTemplateType } from '@/shared/generated'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { processingMessage } from '@/shared/tools/message'

export function OptimizationGroupActionsDropdown() {
	const optimizationGroupId = useOptimizationGroupId()!

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button data-testid='og-actions-trigger' variant='ghost' size='icon'>
					<EllipsisVerticalIcon />
				</Button>
			</DropdownMenuTrigger>
			<OptimizationGroupActionsMenu optimizationGroupId={optimizationGroupId} />
		</DropdownMenu>
	)
}

export function OptimizationGroupActionsMenu({
	optimizationGroupId,
}: {
	optimizationGroupId: string
}) {
	const menuItems = useOptimizationGroupActionsList(optimizationGroupId)

	return (
		<DropdownMenuContent>
			{menuItems.map(({ testId, onClick, icon: Icon, label, ...props }) => (
				<DropdownMenuItem
					key={testId}
					data-testid={testId}
					onClick={onClick}
					{...props}
				>
					<Icon />
					{label}
				</DropdownMenuItem>
			))}
		</DropdownMenuContent>
	)
}

function useOptimizationGroupActionsList(optimizationGroupId: string) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const { showModal } = useModals()
	const { checkPermissons } = useAccess()
	const canManage = checkPermissons(['MANAGE_OPTIMIZATION_GROUP'])
	const isReadyToChange = useOptimizationIsReadyForChange(optimizationGroupId)
	const createTemplateMutation = useCreateSettingsTemplateMutation({
		onMutate() {
			toast.loading(processingMessage(t), {
				id: 'saving_template',
			})
		},
		onSuccess() {
			queryClient.invalidateQueries({
				queryKey: ['settings-templates'],
			})
		},
		onSettled() {
			toast.dismiss('saving_template')
		},
	})
	const deleteOptimizationGroupMutation = useDeleteOptimizationGroupMutation({
		onSuccess() {
			queryClient.invalidateQueries({ queryKey: ['optimization-groups'] })
			queryClient.invalidateQueries({ queryKey: ['main-scenario'] })
		},
	})
	const handleEditInfo = useCallback(() => {
		ampli.ogSettingsDescriptionModalOpen()
		showModal('UPDATE_OPTIMIZATION_GROUP_INFO', {
			optimizationGroupId,
		})
	}, [optimizationGroupId, showModal])
	const handleSaveAsTemplate = useCallback(async () => {
		ampli.ogSettingsSaveScenarioAsTemplate()
		try {
			const pricingCampaigns = await client.pricingCampaign.pricingCampaignList(
				{
					optimization_group_id: optimizationGroupId,
				}
			)
			const scenario = await client.scenario.scenarioGetMain({
				optimizationGroupId,
			})
			const optimizationGroup =
				await client.optimizationGroups.getOptimizationGroup({
					optimizationGroupId,
				})
			const result = await createTemplateMutation.mutateAsync({
				template_type: SettingsTemplateType.Scenario,
				name: optimizationGroup.name,
				body: {
					name: optimizationGroup.name,
					settings: scenario.settings,
					pricing_campaigns: pricingCampaigns
						.filter(negate(isGlobalPricingCampaign))
						.map((campaign) => ({
							name: campaign.name,
							order: campaign.order,
							settings: campaign.settings,
						})),
				},
			})
			if (result) {
				toast.success(
					`${t('templates', {
						defaultValue: 'Template',
					})} ${t('template_saved_successfully', {
						defaultValue: 'was saved successfully',
					})}`,
					{
						action: (
							<LinkButton
								variant='brand'
								onClick={() =>
									showModal('SETTINGS_TEMPLATE_MODAL', {
										id: result.id,
										template_type: result.template_type,
									})
								}
							>
								{result.name}
							</LinkButton>
						),
						duration: 5000,
					}
				)
			}
		} catch (error) {
			toast.error(
				t('fatal_error_title', {
					defaultValue: 'Fatal error',
				})
			)
		}
	}, [optimizationGroupId, t, createTemplateMutation, showModal])
	const handleDelete = useCallback(async () => {
		const answer = await confirm({
			title: t('general_detete_confirm_title', {
				defaultValue: 'Delete confirmation',
			}),
			description: t('general_detete_confirm_subtitle', {
				defaultValue: 'Delete confirmation subtitle',
			}),
			actionText: t('general_delete', {
				ns: 'keys',
			}),
			cancelText: t('general_cancel', {
				ns: 'keys',
			}),
			ActionProps: {
				variant: 'destructive',
			},
		})
		if (answer) {
			ampli.ogSettingsDeleteOg()
			await toast.promise(
				deleteOptimizationGroupMutation.mutateAsync(optimizationGroupId),
				{
					loading: t('deleting.proccess', {
						defaultValue: 'Deleting...',
					}),
					success: t('deleting.success', {
						defaultValue: 'Deleted successfully',
					}),
					error: t('deleting.error', {
						defaultValue: 'Error while deleting',
					}),
				}
			)
		}
	}, [optimizationGroupId, t, deleteOptimizationGroupMutation])
	const showCopySettingsModal = useCallback(() => {
		ampli.settingsCopySettingsStart()
		showModal('COPY_SETTINGS', {
			optimizationGroupId,
		})
	}, [optimizationGroupId, showModal])
	const canCopySettings = checkPermissons([
		'CREATE_PRICING_CAMPAIGNS',
		'EDIT_PRICING_CAMPAIGNS_SETTINGS',
		'EDIT_PRICING_CAMPAIGNS_PRODUCTS',
		'DELETE_PRICING_CAMPAIGNS',
		'CHANGE_DEFAULT_PRICING_CAMPAIGN',
	])

	return useMemo(
		() => [
			{
				label: t('app.edit_name', {
					defaultValue: 'Edit name',
				}),
				icon: EditIcon,
				onClick: handleEditInfo,
				disabled: !canManage,
				testId: 'og-action-edit-name',
			},
			{
				label: t('cp_og_setting_btn', {
					defaultValue: 'Copy settings',
				}),
				icon: CopyIcon,
				onClick: showCopySettingsModal,
				disabled: !canCopySettings,
				testId: 'og-action-copy-settings',
			},
			{
				label: t('settings.action.save_as_template', {
					defaultValue: 'Save as template',
				}),
				icon: PlusIcon,
				onClick: handleSaveAsTemplate,
				testId: 'og-action-save-as-template',
			},
			{
				label: t('general_delete', {
					defaultValue: 'Delete',
				}),
				icon: Trash2Icon,
				onClick: handleDelete,
				disabled: !canManage || !isReadyToChange,
				variant: 'destructive' as const,
				testId: 'og-action-delete',
			},
		],
		[
			canCopySettings,
			canManage,
			isReadyToChange,
			t,
			handleDelete,
			handleSaveAsTemplate,
			handleEditInfo,
			showCopySettingsModal,
		]
	)
}
